import { useId, useMemo } from 'react';
import classNames from '@utils/classnames';

import { LoadingMethod } from 'src/types/enums';
import { Checkbox } from 'components/checkbox/Checkbox';
import { nullthrows } from '../../../../utils/invariant';
import { useTranslation } from '../../../../contexts/translation-context';
import type { TranslationKey } from '../../../../translation/types';

const LOADING_METHOD_ITEMS: {
  translationKey: TranslationKey;
  value: LoadingMethod;
  icon: string;
}[] = [
  {
    translationKey: 'loadingMethods.Back',
    value: LoadingMethod.Back,
    icon: '/static/illustrations/loading-types/back.png',
  },
  {
    translationKey: 'loadingMethods.Side',
    value: LoadingMethod.Side,
    icon: '/static/illustrations/loading-types/side.png',
  },
  {
    translationKey: 'loadingMethods.Top',
    value: LoadingMethod.Top,
    icon: '/static/illustrations/loading-types/top.png',
  },
];

export interface ILoadingMethodIconProps {
  type: LoadingMethod;
  variant?: 'regular' | 'small';
}

export const LoadingMethodIcon: React.FC<ILoadingMethodIconProps> = (props) => {
  const { type, variant = 'regular' } = props;
  const { i18n } = useTranslation();

  const value = nullthrows(
    LOADING_METHOD_ITEMS.find((v) => v.value === type),
    'Loading method not found',
  );
  const title = i18n(value.translationKey);

  return (
    <div className="flex flex-col items-center">
      <img
        src={value.icon}
        alt={title}
        className={classNames({
          'w-24': variant === 'regular',
          'w-12': variant === 'small',
        })}
      />
      <div
        className={classNames('my-2 text-fg-secondary font-regular', {
          'text-sm': variant === 'regular',
          'text-xs': variant === 'small',
        })}
      >
        {title}
      </div>
    </div>
  );
};

export interface ILoadingMethodsProps {
  value: LoadingMethod | null;
  onChange: (newValue: LoadingMethod | null) => void;
  isDisabled?: boolean;
  variant?: 'regular' | 'small';
}

export const LoadingMethods: React.FC<ILoadingMethodsProps> = (props) => {
  const { value, onChange, isDisabled, variant = 'regular' } = props;
  const id = useId();
  const { i18n } = useTranslation();

  const items = useMemo(
    () =>
      LOADING_METHOD_ITEMS.map((v) => ({
        ...v,
        title: i18n(v.translationKey),
      })),
    [i18n],
  );

  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8">
      {items.map((v) => {
        const key = id + v.value;
        const isChecked = value === v.value;

        return (
          <label
            id={key}
            key={key}
            className={classNames(
              'flex flex-col items-center cursor-pointer border rounded-md hover:bg-surface-hover relative select-none',
              {
                'border-border': !isChecked,
                'border-brand': isChecked,
                'p-4': variant === 'regular',
                'p-2': variant === 'small',
              },
            )}
          >
            <div className="flex gap-2 items-center justify-start w-full mb-4">
              <Checkbox
                isChecked={isChecked}
                isDisabled={isDisabled}
                onChange={(newIsChecked) => {
                  if (isDisabled) return;

                  if (newIsChecked) {
                    onChange(v.value);
                  } else {
                    onChange(null);
                  }
                }}
              />

              <div className="font-regular">{v.title}</div>
            </div>

            <img
              src={v.icon}
              alt={v.title}
              className={classNames({
                'w-24': variant === 'regular',
                'w-12': variant === 'small',
              })}
            />
          </label>
        );
      })}
    </div>
  );
};
